import * as React from "react"
import { type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper, { type TimeRange } from "@digits-shared/helpers/dateTimeHelper"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import styled, { type CSSProp } from "styled-components"
import { type DateRangeIntervalOptions } from "src/shared/components/DateRangeSelector/dateRangeConstants"
import { DateRangeSelector } from "src/shared/components/DateRangeSelector/DateRangeSelector"

const SelectorContainer = styled.div`
  position: relative;
  width: -webkit-fill-available;
`

const PeriodRangeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

interface PeriodSelectorBaseProps {
  activeOrigin: IntervalOrigin
  setActiveOrigin: React.SetState<IntervalOrigin>
  onOriginSelected?: (origin: IntervalOrigin) => void

  className?: string
  dateRangeCSS?: CSSProp
  intervals?: DateRangeIntervalOptions
}

export const PeriodSelectorBase: React.FC<React.PropsWithChildren<PeriodSelectorBaseProps>> = ({
  activeOrigin,
  setActiveOrigin,
  onOriginSelected,
  children,

  className,
  dateRangeCSS,
  intervals,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const {
    value: isRangeSelectorShown,
    setFalse: hideRangeSelector,
    setTrue: showRangeSelector,
  } = useStateBoolean()

  const initialOrigin = React.useRef(activeOrigin)
  const onShowRangeSelector = React.useCallback(() => {
    initialOrigin.current = activeOrigin
    showRangeSelector()
  }, [activeOrigin, showRangeSelector])

  const onTimeChange = React.useCallback(
    (timeRange: TimeRange) => {
      setActiveOrigin(dateTimeHelper.intervalOriginFromRange(timeRange))
    },
    [setActiveOrigin]
  )

  const onTimeRange = React.useCallback(
    (timeRange: TimeRange) => {
      const newIntervalOrigin = dateTimeHelper.intervalOriginFromRange(timeRange)
      onOriginSelected?.(newIntervalOrigin)
      setActiveOrigin(newIntervalOrigin)
      hideRangeSelector()
    },
    [onOriginSelected, setActiveOrigin, hideRangeSelector]
  )

  const onCancel = React.useCallback(() => {
    setActiveOrigin(initialOrigin.current)
    hideRangeSelector()
  }, [hideRangeSelector, setActiveOrigin])

  return (
    <PeriodRangeContainer ref={containerRef} className={className}>
      <SelectorContainer role="button" aria-label="Period Selector" onClick={onShowRangeSelector}>
        {children}
      </SelectorContainer>

      {isRangeSelectorShown && (
        <DateRangeSelector
          css={dateRangeCSS}
          positionRef={containerRef}
          defaultValue={activeOrigin}
          onChange={onTimeChange}
          onTimeRange={onTimeRange}
          onCancel={onCancel}
          intervals={intervals}
        />
      )}
    </PeriodRangeContainer>
  )
}
